import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    AppBar,
    Button,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { default as MuiLink } from '@material-ui/core/Link/Link';
import {
    AccountCircle,
    Help as HelpIcon,
    Menu as MenuIcon,
    MoreVert as MoreIcon,
    Search as SearchIcon,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { hasRiskTakingRole, ROLE } from 'shared';
import { ROUTES } from 'shared/src/constants/routes';
import { YIELDS } from 'shared/src/constants/yields';
import { userActions } from '../../state/actions/userActions';
import { downloadExcel } from '../../modules/excelDownload';
import SearchField from '../common/SearchField';
import HelpDialog from '../help/HelpDialog';
import YieldMenu from './YieldMenu';
import SetYieldItems from '../../helpers/SetYieldItems';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    grow: {
        flexGrow: 1,
    },
    menuButton: {
        marginLeft: -12,
        marginRight: 6,
        [theme.breakpoints.up('sm')]: {
            marginRight: 12,
        },
    },
    generaliLogo: {
        height: '35px',
        minWidth: '43.09px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            marginRight: '10px',
        },
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: fade(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: fade(theme.palette.common.white, 0.25),
        },
        marginLeft: 0,
        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing(3),
            width: 'auto',
        },
    },
    searchIcon: {
        width: theme.spacing(6),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputRoot: {
        color: 'inherit',
        width: '100%',
    },
    inputInput: {
        paddingTop: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(10),
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: 200,
        },
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
        },
    },
    sectionMobile: {
        display: 'flex',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    list: {
        width: 250,
    },
    envirosenseLogo: {
        width: 200,
        marginBottom: '10px',
    },
    fullList: {
        width: 'auto',
    },
    contact: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    email: {
        textDecoration: 'underline',
    },
    mobileUserMenu: {
        marginRight: -12,
    },
    woodwiserLogo: {
        height: '40px',
        minWidth: '43.09px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            marginRight: '10px',
        },
    },
    prokatLogoWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    prokatLogo1: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
            height: '40px',
            minWidth: '43.09px',
        },
    },
    prokatLogo2: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
            height: '25px',
            minWidth: '43.09px',
            marginRight: '10px',
        },
    },
    prokatLogo3: {
        height: '60px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    agrarmonitorLogoWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    agrarmonitorLogo: {
        height: '60px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            height: '64px',
        },
    },
    agroLogoWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    agroLogo: {
        height: '60px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            height: '64px',
        },
    },
    cemeteryLogoWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    cemeteryLogo: {
        height: '60px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            height: '64px',
        },
    },
    cemeteryPublicLogoWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
    },
    cemeteryPublicLogo: {
        height: '30px',
        marginRight: '10px',
        [theme.breakpoints.up('sm')]: {
            height: '30px',
        },
    },
    cemeteryLogoText: {
        margin: '0 0 -30 0',
        padding: 0,
        textDecoration: 'none',
        color: theme.palette.common.white,
        '&:hover': {
            textDecoration: 'none',
            cursor: 'pointer',
        },
    },
    link: {
        textDecoration: 'none',
        color: theme.palette.common.white,
        '&:hover': {
            textDecoration: 'none',
            cursor: 'pointer',
        },
    },
    spinner: {
        width: '30px',
        marginLeft: '10px',
        height: '30px',
        [theme.breakpoints.up('sm')]: {
            marginRight: '10px',
        },
    },
    helpButton: {
        color: 'white',
        borderColor: 'white',
        marginLeft: '10px',
    },
    helpIconButton: {
        color: 'white',
        borderColor: 'white',
        '&.MuiIconButton-root': {
            padding: '5px',
        },
    },
    downloadButton: {
        marginLeft: '10px',
        color: 'white',
        borderColor: 'white',
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'inline-flex',
        },
    },
}));

export default function MenuBar() {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
    const [menuOpened, setMenuOpened] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector((state) => state.user.user);
    const theme = useTheme();
    const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);
    const graves = useSelector((state) => state.cemetery.graves);
    const selectedDeads = useSelector((state) => state.cemetery.selectedDeads);
    const selectedGraves = useSelector((state) => state.cemetery.selectedGraves);
    const graveyard = useSelector((state) => state.cemetery.graveyard);

    function handleUserMenuOpen(event) {
        setAnchorEl(event.currentTarget);
    }

    async function handleLogout() {
        handleMenuClose();
        await dispatch(userActions.logoutUser());
        history.push('/');
    }

    function handleMenuClose() {
        setAnchorEl(null);
        handleMobileMenuClose();
    }

    function handleMobileMenuOpen(event) {
        setMobileMoreAnchorEl(event.currentTarget);
    }

    function handleMobileMenuClose() {
        setMobileMoreAnchorEl(null);
    }

    function onMenuClick() {
        setMenuOpened(!menuOpened);
    }

    function onMenuItemClick(url) {
        return () => {
            setMenuOpened(false);
            history.push(url);
        };
    }

    function showUserProfile() {
        history.push(ROUTES.USER_PROFILE());
    }

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    function handleHelpButtonClick() {
        setHelpDialogOpen(true);
    }

    function handleHelpDialogClose() {
        setHelpDialogOpen(false);
    }

    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            {user.role !== ROLE.FARMER && <MenuItem onClick={showUserProfile}>Profil</MenuItem>}
            <MenuItem onClick={handleLogout}>Kijelentkezés</MenuItem>
        </Menu>
    );

    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            {user.role !== ROLE.FARMER && (
                <MenuItem onClick={showUserProfile}>
                    <IconButton color="inherit">
                        <AccountCircle />
                    </IconButton>
                    <p>Profil</p>
                </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
                <IconButton color="inherit">
                    <AccountCircle />
                </IconButton>
                <p>Kijelentkezés</p>
            </MenuItem>
        </Menu>
    );

    function handleDownload(graves, selectedDeads, selectedGraves) {
        return () => downloadExcel(graves, selectedDeads, selectedGraves);
    }

    return (
        <div className={classes.root} style={{ position: 'relative', zIndex: 1300 }}>
            <AppBar position="static">
                <Toolbar>
                    <IconButton
                        className={classes.menuButton}
                        color="inherit"
                        aria-label="Open drawer"
                        onClick={onMenuClick}
                    >
                        <MenuIcon />
                    </IconButton>
                    {user.role !== ROLE.WOODWISER &&
                        user.role !== ROLE.PROKAT &&
                        user.role !== ROLE.AGRARMONITOR &&
                        user.role !== ROLE.AGRO &&
                        user.role !== ROLE.CEMETERY &&
                        user.role !== ROLE.CEMETERY_PUBLIC && (
                            <>
                                <Link to={ROUTES.HOME()} className={classes.link}>
                                    <svg
                                        className={classes.generaliLogo}
                                        viewBox="0 0 218 177"
                                        version="1.1"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill="white"
                                            d="M164.4,110.3c.5,-1.2,1.4,-1.8,2.6,-2l11.9,-2.7l-1,-10.7l-.1,-.3c-.3,-1.3,-1.3,-5.6,-6.8,-6c-21.4,-1.8,-26.4,-5,-31.5,-8.3v-.1l-.5,-.1c-5.5,-3.6,-11,-3.6,-11.5,-3.6c-6.1,0,-13.8,2.4,-14.4,2.5c-1.8,.5,-3.5,1.1,-5.2,1.6c-6.7,2.2,-12.5,4.1,-22.2,3.8H83.9L83.4,86c-.4,1.4,-3,12.1,-.5,19.1c-2.3,1.8,-2.3,3.9,-2.3,4.3c.1,2.3,-.4,2.8,-.6,3c-1.1,1.2,-4.9,1.4,-8,1.4H70.8l-.6,.9c-2,2.7,-6.2,3.2,-8.8,2.6c-1.2,-3.5,-1.2,-6.3,-.1,-7.9c1.3,-2,4.1,-2.1,5.4,-2.1h.6c2.9,0,5.2,-2.5,5.2,-5.3V82.8c.9,-1.4,1.5,-2.9,2.1,-4.6c4.7,-.7,8.3,-4.7,8.3,-9.5c0,-1.9,-.5,-3.7,-1.5,-5.2c4.3,-2.1,8,-6.3,8,-11.9c0,-5.6,-2.7,-8.1,-5.5,-9.9c.8,-2.1,2,-5.9,2.9,-9.7L90.5,18.5c1.7,-5.3,6.1,-8.6,11.2,-8.6h66.2c-2.8,3.9,-6.8,5.7,-12.3,5.7h-40l-2.1,4.5h35.9c-2.9,4.6,-7,6.6,-12.9,6.6h-26l-2,4.6h23.2c-2.9,4.2,-7,6.3,-12.7,6.3H104.8L102.7,42h14.9c.1,.3,.5,.9,.5,2.8c0,2.8,-2.4,5.2,-5.4,5.2c-1.1,0,-1.8,-.3,-3.4,-.9c-.6,-.2,-1.3,-.6,-1.9,-.8l-3.6,3.1c.7,.2,1.4,.5,2,.8c3,1.3,4.6,1.9,6.9,1.9c2.8,0,5.2,-1.1,7,-2.9c2.5,.2,6.6,.5,10.6,.8c4.8,.5,9.3,.8,11.6,.9c1.1,.1,2.2,.1,3.3,.2c2,0,3.7,.2,5.6,.3c9,14.7,13.6,16.6,31.1,18.7c1.2,2.3,2.5,4.7,3.4,7v28.7c.2,1.3,.2,1.8,0,2.1c-.6,.8,-3.3,1.9,-4.2,2.3l-14.8,5.5c-2,-2.6,-2.6,-5.4,-1.9,-7.4Zm-3.1,7.7H132.1l12.5,-13.2c2.6,-2.5,3.3,-5.7,2.2,-8.8c-.5,-1.7,-2.2,-5.1,-3.9,-8.6c4.9,2.3,12.5,4.4,27.8,5.6c1.8,.2,2.3,.8,2.7,2.5l.6,6.6l-7.9,1.8c-2.8,.5,-4.8,2.3,-5.8,4.8c-1.1,2.9,-.7,6.2,1,9.3Zm-52.6,0c-.8,-3.1,-.5,-5.7,1,-7.3c1.4,-1.6,3.5,-2,4.9,-1.8l8.6,1.6h1.4l6,-6.5h.1c1,-1.2,4.2,-4.9,1,-9.5L123.5,81.2c1.5,-.2,2.8,-.3,4,-.3c.1,0,4.2,0,8.5,2.5c.5,1.2,1.4,2.9,2.3,4.7c1.6,3.4,3.7,7.7,4.4,9.4c.1,.5,.6,2.2,-1.4,4.3L125.9,118H108.7Zm-2.4,-10.2c-1.7,1.8,-3.2,5.1,-2.2,10.2H76.7c2.9,-.2,5,-.9,6.6,-2.5c1.7,-1.9,1.8,-4.4,1.8,-6.1c0,-.3,.4,-.9,1.7,-1.5l2.4,-1.1l-1.4,-2.2C85.5,101.1,86.5,93.2,87.3,88.9h.3c9.3,0,15.2,-1.9,21.6,-4c1.7,-.5,3.4,-1.1,5.2,-1.6h.1c0,0,1.9,-.6,4.4,-1.2l8.9,14.7l.1,.2c.9,1.2,1,2.1,-.6,4l-4.5,4.8l-7.3,-1.4h-.1c-3.3,-.4,-6.9,.9,-9.1,3.4Zm-48.7,-1c-1.9,2.8,-2.2,6.5,-.7,11.2H52.2V81.9c1.7,-.3,3.5,-.7,5.2,-1.5c-.1,1.6,-.3,2.8,-.6,4.5c0,.4,-.2,.8,-.3,1.2l-.4,2.6h2.6c4,0,7,-.6,9.3,-1.8V102c0,.4,-.4,.8,-.7,.8h-.6c-4,0,-7.3,1.4,-9.1,4Zm-9.9,7.4L26.6,108.7L5.5,114.2V76.4L26.6,70.9l21.1,5.5v37.8ZM8.2,118l18.4,-4.7L45.1,118H8.2ZM15.6,62.4c.9,-1.3,2.9,-2.4,5.4,-2l2,.3l.5,-1.8c.1,-.6,.4,-.9,.7,-1.2c.5,-.5,1.3,-.7,2.4,-.7c2.7,.1,6.1,2,8.8,4.9c1,1,1.9,2.4,2.7,3.6c.2,.2,.3,.5,.5,.7c.2,.4,.3,.7,.4,.8c.2,.1,1.5,2.2,1.4,2.3c.8,-1.3,1.8,-2.6,2.7,-3.8c.1,-.1,0,-.3,0,-.4c-.2,-.5,-.8,-1.3,-1.1,-1.7c0,-.1,-.1,-.2,-.1,-.4q-.1,0,-.1,0c0,0,-.1,-.2,-.2,-.5c-.4,-.4,-.8,-1.1,-1,-1.5l5.8,-9.4c1.4,.3,2.8,.3,3.3,.3h.1c8.3,0,16.9,-4.7,16.9,-12.6v-.2c-.2,-2.2,-.6,-4.2,-.9,-6C65.6,32,65.3,31,65.3,30.5c0,-.9,1.4,-1.8,3,-1.8h2.8L69.8,21.8c3.8,-2.8,6.1,-7.3,6,-11.6c3.9,3.1,6.8,8.5,7.4,14.2c.2,1.7,-.2,4,-.7,6.4l-.3,1.1c-.9,4,-2.3,8,-3.1,10l-.7,1.8l1.7,1c3.1,1.6,4.8,2.9,4.8,6.9c0,4.5,-4.1,8,-8,8.7l-5.4,1.1l4.6,3c1.4,1,2.3,2.6,2.3,4.3c0,2.8,-2.4,5.1,-5.4,5.1H71.3l-.5,1.6c-1.6,5.5,-3.5,8.1,-9.3,8.8c.2,-1.7,.4,-3.3,.4,-5.6c0,-.5,-.1,-2,-.1,-2.5L61.6,71L58,74.7c-1.2,1.2,-3.3,2.2,-5.8,2.6V72.8L26.6,66.2l-9.1,2.5c-2,-2.1,-2.4,-5.4,-1.9,-6.3ZM43.2,46c.1,-.3,.4,-.6,.7,-.6l3.8,.2c4.7,-.3,9.5,-2.1,10,-7.1C57.7,35.2,55.3,32,52,32c-1.7,0,-3.7,1.2,-5.9,2.8c-1.6,.9,-2.7,1.6,-3.5,1.6c-.7,0,-1.1,-.3,-1.1,-.6q0,-.1,0,-.3L44,32.2L41.8,30.8c-.4,-.2,-1.1,-.8,-1.2,-1.2c0,-.2,.2,-.5,.3,-.7c.9,-1.1,1.9,-2.3,3,-3.8c.5,-.9,1,-1.4,2,-1.4c1.1,0,1.8,.8,1.8,1.8c0,.8,.8,1.2,1.8,1.2c.9,0,1.3,-.5,1.9,-1.2c0,0,2.2,-1.9,1,-2.9c-1.4,-1,-6.6,1.2,-5.2,-3.8l.4,-.6c.6,-.8,3.1,-2.7,8.4,-2.7c1,0,2.1,.1,3.1,.2l.7,.2l11.3,-7c.1,.2,.1,.5,.1,.5c.6,3.4,-1.6,7.4,-5,9.3l-1.3,.9l.9,4.9c-2.9,.9,-4.9,3.2,-4.9,6c0,1,.3,2.1,.5,3.5c.3,1.7,.6,3.4,.7,5.2c0,5.3,-7.2,8.8,-13.3,8.6h-.2c-.2,0,-4.1,-.4,-5,-1.1c-.3,-.2,-.4,-.5,-.4,-.7ZM52.5,11c.7,-.9,1.8,-1.9,3.3,-2.7c1,-.8,2.4,-1.5,3.7,-1.8c2.2,-.7,4.7,-.7,7.3,-.2l-8,4.9C57.9,11.1,56.9,11,56,11c-1.3,0,-2.7,.1,-3.8,.3c.2,-.1,.3,-.2,.3,-.3ZM173.1,9.9h29.8c-2.7,3.9,-6.7,5.7,-12.2,5.7H168.6c1.8,-1.5,3.3,-3.4,4.5,-5.7Zm-1.5,16.8h-22c2,-1.7,3.6,-3.9,4.8,-6.6h30.1c-2.9,4.6,-7,6.6,-12.9,6.6ZM154.1,37.6h-22c1.9,-1.7,3.4,-3.8,4.7,-6.3h30c-2.9,4.2,-7,6.3,-12.7,6.3Zm5.1,16.8c7,1.2,12.7,4.2,17,9.3c.9,1.1,1.8,2.3,2.7,3.5C167.1,65.7,163,63.9,156.4,54c.9,.1,1.8,.2,2.8,.4ZM206,102.8c.5,.6,.8,1.3,1.2,1.9c.5,.8,.8,1.6,1.4,2.3c-3.5,-.2,-6,-2,-8.4,-5.7c2.2,-.7,4.8,-.2,5.8,1.5ZM178.1,118l4.6,-1.6v-.1c6.6,-2.7,7.5,-4.3,7.2,-8.7V90.1c4.9,12.7,9.6,21.5,19.2,21.5c1.3,0,2.6,-.3,4.2,-.6l3.7,-.8l-2.5,-2.8c-1.7,-1.9,-2.5,-3.3,-3.4,-4.9c-.3,-.6,-.8,-1.4,-1.2,-2.2C207.6,96.7,202.3,95.5,198,97.5c-2,-4,-4,-9,-6.4,-15.3L191.1,81c-.4,-.9,-.8,-1.9,-1.2,-2.8V78c-1.6,-3.3,-3.1,-6.6,-5,-9.7c-1.8,-2.8,-3.4,-5.3,-5.3,-7.5C174.6,54.9,168,51.3,159.9,50c-5.6,-.9,-9.5,-1.1,-14.5,-1.4c-1.1,-.1,-2.1,-.1,-3.3,-.2c-2.2,-.1,-6.9,-.4,-11.4,-.9c-3.1,-.2,-6.2,-.4,-8.6,-.6c.1,-.7,.2,-1.5,.2,-2.1c0,-1,-.1,-2,-.2,-2.8h32c8.3,0,14.2,-3.6,17.8,-10.7c8.2,-.2,14.1,-3.9,17.6,-11.2h1.2c8.5,0,14.7,-3.8,18.2,-11.5l1.4,-3.2H101.7C94.6,5.4,88.5,10,86.2,17.3v.2C84.4,12.3,81,7.9,76.7,5.4C70.6,1.8,63.8,.6,58.3,2.2c-1.8,.5,-3.6,1.3,-5.1,2.3C52,5.3,50.3,6.6,48.9,8.3c-1.3,1.7,-2.7,3.9,-3.7,5.9c-.5,.6,-1.1,1,-1.4,1.5l-1.3,2.5v.2c-1.6,3.5,-3.5,5.9,-5,7.6c-1.4,1.7,-1.5,3.3,-1.3,4.4c.2,1.2,.9,2.1,1.5,2.8h-.1c-.4,.8,-.6,1.7,-.6,2.6c0,2.8,2.5,5,5.6,5c2.2,0,4,-1.1,5.9,-2.4c.8,-.4,2.9,-1.9,3.5,-1.9c.9,0,1.7,.8,1.7,1.6c0,2.4,-3,3.6,-5.2,3.8l-4.4,.5c-3.2,.3,-4.7,1.9,-4.7,3.5c0,1.4,.7,2.3,1.3,2.8l.2,.4l1.2,1l-4.7,7.4c-3.3,-3,-7.3,-4.9,-10.7,-5c-2.2,0,-4.2,.6,-5.6,2c-.4,.3,-.8,.8,-1.1,1.3h-.1c-3.6,0,-6.6,2,-8,4.2c-1.7,2.4,-1.1,6.6,1,9.9L1,72.8v56.6H217.6V118H178.1Zm-136,39.1V142.2c9.4,0,12.8,-.4,14.4,11h1V140.5H32.1v1.7h4.4V175H31.7v1.8H57.9V163.4H56.5C55.2,173.5,52.7,175,45.4,175H42.1V159.1h2c3.2,.1,5.1,2,5.7,7.6h1.1V150.2H49.8c0,.1,-.2,7,-5.9,6.9H42.1Zm-10.3,.7H17.4v1.6h4.9v11.8c-.9,2.9,-5.3,4.3,-6.6,4.3C6.6,175.5,7.3,162,7.3,158.4c0,-3.3,-.4,-16.9,8.4,-16.9c6.3,0,9.6,6.1,10.8,11.7h1c0,-2.3,.1,-13.2,.1,-13.2c0,-.1,-4.3,3.1,-4.3,3c.1,0,-2,-2.8,-7.6,-2.8C4.9,140.2,1,150.9,1,158.4c.1,6.9,3.2,18.7,14.7,18.7c3.7,0,6.2,-1.9,7.6,-3.9c0,-.2,4.5,3.5,4.5,3.6V159.4h4v-1.6Zm125.3,5.9L162.4,148l5.5,15.7H157.1Zm-20.4,-6.3h-5.8V142.2h7.5c4.4,.7,4.6,5.8,4.6,7.4c0,4,-1.7,7.8,-6.3,7.8Zm62.7,6c-1.3,10.1,-3.9,11.6,-11.1,11.6h-.4V142.2h4.6v-1.7H177.4v1.7h4.5V175h-3.6L164.9,139.5H163c0,0,-11.2,32.2,-11.2,32.3c0,-.1,-.8,2.8,-2.6,3.1c-1.8,.1,-1.6,-.8,-1.6,-1.1v-6.3c0,-1.5,-.2,-5.2,-1.9,-7c-1.8,-1.9,-5.1,-2,-5,-2.1c0,0,8.2,-1.6,8.2,-9.4c0,-5.5,-3.8,-8.3,-10.2,-8.5h-18v1.7h4.6V175h-4.6v1.8h14.8V175h-4.7V159.2h6.3c10.6,1,-1.9,17.6,12.2,17.6h8.8V175c-.6,0,-3.8,.1,-4,-2.2c-.1,-1.6,2.2,-7.5,2.2,-7.5h12L172,175h-4.1v1.8h32.7V163.4h-1.2ZM102.7,142.2c9.4,0,12.9,-.4,14.4,11h1V140.5h-37v1.7h1c1.3,0,4.4,.2,4.6,2.8v21L69.3,140.5H59.8v1.7h1.7c1.9,0,4.4,0,4.5,2.8v26.8c0,2.7,-3.9,3.7,-5.5,3.7v1.3H73.3v-1.3c-1.7,0,-5.7,-1,-5.7,-3.7l.2,-23.5c6.8,9.8,19.8,28.5,19.9,28.5h.6V145c.1,-2.6,3.3,-2.8,4.6,-2.8h4.2V175H92.3v1.8h26.2V163.4h-1.4c-1.2,10.1,-3.8,11.6,-11,11.6h-3.4V159.1h2c3.2,.1,5.1,2,5.7,7.6h1.2V150.2h-1.2c0,.1,-.2,7,-5.8,6.9h-1.9V142.2Zm114.4,0v-1.7H202.4v1.7H207V175h-4.6v1.8h14.7V175h-4.6V142.2h4.6Zm-174,-60L28.9,78.4v4.7l14.2,3.7V82.2ZM10.2,86.8L24.3,83.1V78.4L10.2,82.2v4.6Zm0,9.2L24.3,92.4V87.8L10.2,91.4V96ZM43.1,91.4L28.9,87.8v4.6L43.1,96V91.4Zm0,9.4L28.9,97.1v4.7l14.2,3.6v-4.6Zm-32.9,4.6l14.1,-3.6V97.1l-14.1,3.7v4.6Z"
                                            className="g0"
                                        />
                                    </svg>
                                </Link>
                                <Link to={ROUTES.HOME()} className={classes.link}>
                                    <Typography
                                        className={classes.title}
                                        variant="h6"
                                        color="inherit"
                                        noWrap={true}
                                    >
                                        {user.role === ROLE.FARMER ? 'Kárriasztás' : 'GeoRisk'}
                                    </Typography>
                                </Link>
                            </>
                        )}
                    {user.role === ROLE.WOODWISER && (
                        <>
                            <Link to={ROUTES.HOME()}>
                                <img
                                    alt="logo"
                                    src="/images/woodwiser/woodwiser_logo.png"
                                    className={classes.woodwiserLogo}
                                />
                            </Link>
                        </>
                    )}
                    {user.role === ROLE.PROKAT && (
                        <>
                            <Link to={ROUTES.HOME()} className={classes.prokatLogoWrapper}>
                                <img
                                    alt="logo"
                                    src="/images/prokat/logo1.png"
                                    className={classes.prokatLogo1}
                                />
                                <img
                                    alt="logo"
                                    src="/images/prokat/logo2.png"
                                    className={classes.prokatLogo2}
                                />
                                <img
                                    alt="logo"
                                    src="/images/prokat/HL-lab_Logo_300dpi_white.png"
                                    className={classes.prokatLogo3}
                                />
                            </Link>
                        </>
                    )}
                    {user.role === ROLE.AGRARMONITOR && (
                        <>
                            <Link to={ROUTES.HOME()} className={classes.agrarmonitorLogoWrapper}>
                                <img
                                    alt="logo"
                                    src="/images/envirosense-logo-c.png"
                                    className={classes.agrarmonitorLogo}
                                />
                            </Link>
                        </>
                    )}
                    {user.role === ROLE.AGRO && (
                        <>
                            <Link to={ROUTES.HOME()} className={classes.agroLogoWrapper}>
                                <img
                                    alt="logo"
                                    src="/images/envirosense-logo-c.png"
                                    className={classes.agroLogo}
                                />
                            </Link>
                        </>
                    )}
                    {(user.role === ROLE.CEMETERY || user.role === ROLE.CEMETERY_PUBLIC) && (
                        <>
                            <Link to={ROUTES.HOME()} className={classes.cemeteryPublicLogoWrapper}>
                                <img
                                    alt="logo"
                                    src="/images/envimaplogo-inv.png"
                                    className={classes.cemeteryPublicLogo}
                                />
                            </Link>
                        </>
                    )}
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <SearchIcon />
                        </div>
                        <SearchField />
                    </div>
                    {user.role === ROLE.CEMETERY && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleDownload(graves, selectedDeads, selectedGraves)}
                            className={classes.downloadButton}
                        >
                            Letöltés
                        </Button>
                    )}
                    {user.role === ROLE.FARMER && isTabletOrDesktop && (
                        <Button
                            variant="outlined"
                            className={classes.helpButton}
                            onClick={handleHelpButtonClick}
                        >
                            Segítség
                        </Button>
                    )}
                    {user.role === ROLE.FARMER && !isTabletOrDesktop && (
                        <IconButton
                            variant="outlined"
                            className={classes.helpIconButton}
                            onClick={handleHelpButtonClick}
                        >
                            <HelpIcon />
                        </IconButton>
                    )}
                    <div className={classes.grow} />
                    {user.role !== ROLE.FARMER && (
                        <div className={classes.sectionDesktop}>
                            <Typography
                                className={classes.title}
                                variant="subtitle1"
                                color="inherit"
                                noWrap={true}
                            >
                                {user ? user.name : null}
                            </Typography>
                        </div>
                    )}
                    {user.role !== ROLE.CEMETERY_PUBLIC && (
                        <div className={classes.sectionDesktop}>
                            <IconButton
                                aria-owns={isMenuOpen ? 'material-appbar' : undefined}
                                aria-haspopup="true"
                                onClick={handleUserMenuOpen}
                                color="inherit"
                            >
                                <AccountCircle />
                            </IconButton>
                        </div>
                    )}
                    {user.role !== ROLE.CEMETERY_PUBLIC && (
                        <div className={classes.sectionMobile}>
                            <IconButton
                                className={classes.mobileUserMenu}
                                aria-haspopup="true"
                                onClick={handleMobileMenuOpen}
                                color="inherit"
                            >
                                <MoreIcon />
                            </IconButton>
                        </div>
                    )}
                </Toolbar>
            </AppBar>
            {renderMenu}
            {renderMobileMenu}
            <Drawer open={menuOpened} onClose={onMenuClick}>
                <div className={classes.list}>
                    <List>
                        <ListItem className={classes.contact}>
                            {user.role !== ROLE.WOODWISER &&
                                user.role !== ROLE.PROKAT &&
                                user.role !== ROLE.AGRARMONITOR &&
                                user.role !== ROLE.AGRO &&
                                user.role !== ROLE.CEMETERY &&
                                user.role !== ROLE.CEMETERY_PUBLIC && (
                                    <a href="https://envirosense.hu">
                                        <img
                                            alt="logo"
                                            src="/images/envirosense_logo.jpg"
                                            className={classes.envirosenseLogo}
                                        />
                                    </a>
                                )}
                            <Typography variant={'h6'}>Kapcsolat</Typography>
                            {user.role !== ROLE.FARMER &&
                                user.role !== ROLE.WOODWISER &&
                                user.role !== ROLE.PROKAT &&
                                user.role !== ROLE.AGRARMONITOR &&
                                user.role !== ROLE.AGRO &&
                                user.role !== ROLE.CEMETERY &&
                                user.role !== ROLE.CEMETERY_PUBLIC && (
                                    <Typography variant={'body2'}>Szanka Gábor</Typography>
                                )}
                            {user.role === ROLE.PROKAT && (
                                <Typography variant={'body2'}>Szabó Gergely</Typography>
                            )}
                            {user.role === ROLE.AGRARMONITOR && (
                                <Typography variant={'body2'}>Tran Balázs</Typography>
                            )}
                            {user.role === ROLE.AGRO && (
                                <Typography variant={'body2'}>Szanka Gábor</Typography>
                            )}
                            {(user.role === ROLE.CEMETERY ||
                                user.role === ROLE.CEMETERY_PUBLIC) && (
                                <Typography variant={'body2'}></Typography>
                            )}
                            {user.role !== ROLE.FARMER &&
                                user.role !== ROLE.WOODWISER &&
                                user.role !== ROLE.PROKAT &&
                                user.role !== ROLE.AGRARMONITOR &&
                                user.role !== ROLE.AGRO &&
                                user.role !== ROLE.CEMETERY &&
                                user.role !== ROLE.CEMETERY_PUBLIC && (
                                    <Typography variant={'body2'}>Tel: +36 30 164 34 25</Typography>
                                )}
                            {user.role === ROLE.WOODWISER && (
                                <Typography variant={'body2'}>Tel: +36 20 314 2991</Typography>
                            )}
                            {user.role === ROLE.PROKAT && (
                                <Typography variant={'body2'}>Tel: +36 70 634 8702</Typography>
                            )}
                            {user.role === ROLE.AGRARMONITOR && (
                                <Typography variant={'body2'}>Tel: +36 70 600 1661</Typography>
                            )}
                            {user.role !== ROLE.FARMER &&
                                user.role !== ROLE.WOODWISER &&
                                user.role !== ROLE.PROKAT &&
                                user.role !== ROLE.AGRARMONITOR &&
                                user.role !== ROLE.AGRO &&
                                user.role !== ROLE.CEMETERY &&
                                user.role !== ROLE.CEMETERY_PUBLIC && (
                                    <Typography variant={'body2'}>
                                        Email:{' '}
                                        <MuiLink
                                            href="mailto:georisk@envimap.hu"
                                            className={classes.email}
                                        >
                                            georisk@envimap.hu
                                        </MuiLink>
                                    </Typography>
                                )}
                            {user.role === ROLE.FARMER && (
                                <Typography variant={'body2'}>
                                    Email:{' '}
                                    <MuiLink
                                        href="mailto:generali@karriasztas.hu"
                                        className={classes.email}
                                    >
                                        generali@karriasztas.hu
                                    </MuiLink>
                                </Typography>
                            )}
                            {user.role === ROLE.WOODWISER && (
                                <Typography variant={'body2'}>
                                    Email:{' '}
                                    <MuiLink
                                        href="mailto:info@woodwiser.hu"
                                        className={classes.email}
                                        style={{ color: 'black' }}
                                    >
                                        info@woodwiser.hu
                                    </MuiLink>
                                </Typography>
                            )}
                            {user.role === ROLE.PROKAT && (
                                <Typography variant={'body2'}>
                                    Email:{' '}
                                    <MuiLink
                                        href="mailto:szabog@talajvizsgalo.hu"
                                        className={classes.email}
                                        style={{ color: 'black' }}
                                    >
                                        szabog@talajvizsgalo.hu
                                    </MuiLink>
                                </Typography>
                            )}
                            {user.role === ROLE.AGRARMONITOR && (
                                <Typography variant={'body2'}>
                                    Email:{' '}
                                    <MuiLink
                                        href="mailto:agrarmonitor@envimap.hu"
                                        className={classes.email}
                                        style={{ color: 'black' }}
                                    >
                                        agrarmonitor@envimap.hu
                                    </MuiLink>
                                </Typography>
                            )}
                            {user.role === ROLE.AGRO && (
                                <Typography variant={'body2'}>
                                    Email:{' '}
                                    <MuiLink
                                        href="mailto:gabor.szanka@envirosense.hu"
                                        className={classes.email}
                                        style={{ color: 'black' }}
                                    >
                                        gabor.szanka@envirosense.hu
                                    </MuiLink>
                                </Typography>
                            )}
                            {(user.role === ROLE.CEMETERY || user.role === ROLE.CEMETERY_PUBLIC) &&
                                graveyard &&
                                graveyard.email && (
                                    <Typography variant={'body2'}>
                                        Email: <br />
                                        <MuiLink
                                            href={`mailto:${graveyard.email}`}
                                            className={classes.email}
                                        >
                                            {graveyard.email}
                                        </MuiLink>
                                    </Typography>
                                )}
                        </ListItem>
                        {user.role !== ROLE.FARMER &&
                            user.role !== ROLE.EXPERT_UW &&
                            user.role !== ROLE.EXPERT_DAMAGE &&
                            user.role !== ROLE.WOODWISER &&
                            user.role !== ROLE.PROKAT &&
                            user.role !== ROLE.AGRARMONITOR &&
                            user.role !== ROLE.AGRO &&
                            user.role !== ROLE.CEMETERY &&
                            user.role !== ROLE.CEMETERY_PUBLIC && (
                                <ListItem button={true} onClick={onMenuItemClick(ROUTES.RADAR())}>
                                    <ListItemText primary={'Erőforrás allokáció támogatása'} />
                                </ListItem>
                            )}
                        {user.role !== ROLE.FARMER &&
                            user.role !== ROLE.EXPERT_UW &&
                            user.role !== ROLE.EXPERT_DAMAGE &&
                            user.role !== ROLE.WOODWISER &&
                            user.role !== ROLE.PROKAT &&
                            user.role !== ROLE.AGRARMONITOR &&
                            user.role !== ROLE.AGRO &&
                            user.role !== ROLE.CEMETERY &&
                            user.role !== ROLE.CEMETERY_PUBLIC && (
                                <ListItem
                                    button={true}
                                    onClick={onMenuItemClick(ROUTES.CREATE_DAMAGE())}
                                >
                                    <ListItemText primary={'Új kárakta'} />
                                </ListItem>
                            )}
                        {user.role === ROLE.ADMIN && (
                            <ListItem button={true} onClick={onMenuItemClick(ROUTES.DASHBOARD())}>
                                <ListItemText primary={'Vezetői dashboard'} />
                            </ListItem>
                        )}
                        {user.role === ROLE.ADMIN && (
                            <ListItem
                                button={true}
                                onClick={onMenuItemClick(ROUTES.DAMAGE_CHRONOLOGY())}
                            >
                                <ListItemText primary={'Kárkronológia térkép'} />
                            </ListItem>
                        )}
                        {hasRiskTakingRole(user) &&
                        <ListItem button={true} onClick={onMenuItemClick(ROUTES.FROST_DAMAGE())}>
                            <ListItemText primary={'Fagyveszély térkép'}/>
                        </ListItem>
                        }
                        {user.role !== ROLE.FARMER &&
                            user.role !== ROLE.EXPERT_UW &&
                            user.role !== ROLE.EXPERT_DAMAGE &&
                            user.role !== ROLE.WOODWISER &&
                            user.role !== ROLE.PROKAT &&
                            user.role !== ROLE.AGRARMONITOR &&
                            user.role !== ROLE.AGRO &&
                            user.role !== ROLE.CEMETERY &&
                            user.role !== ROLE.CEMETERY_PUBLIC &&
                            Object.keys(YIELDS.TYPES).map((type, t) => (
                                <ListItem key={t} button={true} onclick={(e) => e.preventDefault()}>
                                    <YieldMenu
                                        label={`${YIELDS.TYPES[type]} hozamtérkép`}
                                        items={SetYieldItems(type)}
                                        onItemClick={() => setMenuOpened(false)}
                                    />
                                </ListItem>
                            ))}
                        {user.role === ROLE.FARMER && (
                            <ListItem className={classes.contact}>
                                <Typography variant={'body2'}>
                                    <MuiLink
                                        href="/documents/GENERALI_RIASZTAS_ASZF_2021.pdf"
                                        className={classes.email}
                                    >
                                        Általános Szerződési Feltételek
                                    </MuiLink>
                                </Typography>
                            </ListItem>
                        )}
                    </List>
                </div>
            </Drawer>
            <HelpDialog open={helpDialogOpen} handleClose={handleHelpDialogClose} />
        </div>
    );
}
